import moment from "moment";
import { _global } from "../../helpers";
import { changeToTitleCase } from "../../utils/convertionUtils";

export const COMPLETE_EXPORT_COLUMNS = [
  { header: "Initial Message Date", width: 22 },
  { header: "Completed Date", width: 18 },
  { header: "Last Message Date", width: 18 },
  { header: "Attachment", width: 12 },
  { header: "Member Name", width: 28 },
  { header: "Client Name", width: 28 },
  { header: "Member ID", width: 18 },
  { header: "Vendor", width: 20 },
  { header: "Member Email", width: 34 },
  { header: "Phone", width: 16 },
  { header: "Subject", width: 60 },
  { header: "Representative", width: 32 },
  { header: "Response Date", width: 18 },
  { header: "Turnaround Time (Business Days)", width: 32 },
  { header: "Member Initial Message", width: 48 },
  { header: "Member Care Response", width: 48 },
];

export const MEMBER_CARE_INITIATED_COLUMNS = [
  { header: "Message Date", width: 18 },
  { header: "Representative", width: 32 },
  { header: "Attachment", width: 12 },
  { header: "Member Name", width: 28 },
  { header: "Client Name", width: 28 },
  { header: "Member ID", width: 18 },
  { header: "Vendor", width: 20 },
  { header: "Member Email", width: 34 },
  { header: "Phone", width: 16 },
  { header: "Subject", width: 60 },
  { header: "Message", width: 48 },
];

export const ALL_MESSAGES_COLUMNS = [
  { header: "Thread", width: 12 },
  { header: "Message Date", width: 18 },
  { header: "Sender Type", width: 18 },
  { header: "Sender", width: 32 },
  { header: "Attachment", width: 12 },
  { header: "Member Name", width: 28 },
  { header: "Client Name", width: 28 },
  { header: "Member ID", width: 18 },
  { header: "Vendor", width: 20 },
  { header: "Member Email", width: 34 },
  { header: "Phone", width: 16 },
  { header: "Subject", width: 60 },
  { header: "Message", width: 48 },
];

export const FETCH_CONCURRENCY = 5;
export const NO_MEMBER_CARE_RESPONSE = "No Member Care response";
export const MEMBER_REQUEST_INITIAL_DATE_COLUMN = 1;
export const MEMBER_REQUEST_RESPONSE_DATE_COLUMN = 13;
export const MEMBER_REQUEST_TURNAROUND_COLUMN = 14;

const pad = (value) => String(value).padStart(2, "0");

const buildExportStamp = (date) => {
  const offsetMinutes = -date.getTimezoneOffset();
  const sign = offsetMinutes >= 0 ? "+" : "-";
  const absOffset = Math.abs(offsetMinutes);
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}-${pad(date.getMinutes())}-${pad(date.getSeconds())}` +
    `${sign}${pad(Math.floor(absOffset / 60))}-${pad(absOffset % 60)}`
  );
};

export const buildCompleteExportFileName = (date = new Date()) =>
  `CSR_Complete_${buildExportStamp(date)}.xlsx`;

const hasAttachment = (attachments) =>
  (!isNaN(attachments) && attachments > 0) || (Array.isArray(attachments) && attachments.length > 0);

const getMemberName = (conversation, member) => {
  if (conversation.member) {
    const fullName = [conversation.member.firstName, conversation.member.lastName].filter(Boolean).join(" ");
    return changeToTitleCase(fullName) || "";
  }
  return (member && changeToTitleCase(member.name)) || "";
};

const toLocalDate = (value) => {
  if (value === null || value === undefined || value === "") return "";
  const parsed = moment(value);
  return parsed.isValid() ? parsed.startOf("day").toDate() : "";
};

const messageText = (value) => String(value || "").replace(/\{\{break\}\}/g, "\n");

const formatCycleMessages = (messages) => {
  const texts = messages.map((message) => messageText(message.message));
  if (texts.length <= 1) return texts[0] || "";
  return texts.map((text, index) => `${index + 1}. ${text}`).join("\n\n");
};

const anyAttachment = (messages) => messages.some((message) => hasAttachment(message.attachments));

const isMemberMessage = (message, recipientId) => message?.senderId === recipientId;

const completionTimestamp = (conversation) => {
  if (conversation.transactionDate) return conversation.transactionDate;
  if (String(conversation.conversationId || "").includes("LOCAL_")) return conversation.createdTimestamp;
  return "";
};

// A cycle collects every member message until Member Care replies, then every
// Member Care reply until the next member message. A trailing member request
// with no reply stays unanswered. Member Care messages sent while no request
// is open are outreach.
export const classifyMessages = (messages, recipientId) => {
  const ordered = [...(messages || [])].sort(
    (a, b) => (a.createdTimestamp || 0) - (b.createdTimestamp || 0)
  );
  const cycles = [];
  const memberCareInitiated = [];
  let memberMessages = [];
  let careMessages = [];

  const closeAnsweredCycle = () => {
    if (!memberMessages.length || !careMessages.length) return;
    cycles.push({ memberMessages, careMessages });
    memberMessages = [];
    careMessages = [];
  };

  ordered.forEach((message) => {
    if (isMemberMessage(message, recipientId)) {
      closeAnsweredCycle();
      memberMessages.push(message);
      return;
    }
    if (!memberMessages.length) {
      memberCareInitiated.push(message);
      return;
    }
    careMessages.push(message);
  });

  closeAnsweredCycle();
  if (memberMessages.length) cycles.push({ memberMessages, careMessages: [] });

  return { ordered, cycles, memberCareInitiated };
};

export const buildCycles = (messages, recipientId) => classifyMessages(messages, recipientId).cycles;

export const isTimestampInDateRange = (timestamp, fromDate, toDate) => {
  if (!fromDate && !toDate) return true;
  const day = moment(timestamp);
  if (!day.isValid()) return false;
  const dayStart = day.clone().startOf("day");
  if (fromDate && dayStart.isBefore(moment(fromDate, "YYYY-MM-DD").startOf("day"))) return false;
  if (toDate && dayStart.isAfter(moment(toDate, "YYYY-MM-DD").startOf("day"))) return false;
  return true;
};

const columnLetter = (columnNumber) => {
  let remainder = columnNumber;
  let letters = "";
  while (remainder > 0) {
    const index = (remainder - 1) % 26;
    letters = String.fromCharCode(65 + index) + letters;
    remainder = Math.floor((remainder - 1) / 26);
  }
  return letters;
};

const networkDays = (startDay, endDay) => {
  if (endDay.isBefore(startDay, "day")) return -networkDays(endDay, startDay);
  let days = 0;
  const cursor = startDay.clone();
  while (cursor.isSameOrBefore(endDay, "day")) {
    const dayOfWeek = cursor.day();
    if (dayOfWeek !== 0 && dayOfWeek !== 6) days += 1;
    cursor.add(1, "day");
  }
  return days;
};

// Matches NETWORKDAYS(initial, response)-1. Friday to Tuesday is 2. A same-day weekday is 0.
export const businessDayTurnaround = (initialTimestamp, responseTimestamp) => {
  const start = moment(initialTimestamp);
  const end = moment(responseTimestamp);
  if (!start.isValid() || !end.isValid()) return null;
  return networkDays(start.clone().startOf("day"), end.clone().startOf("day")) - 1;
};

export const turnaroundFormula = (rowNumber) => {
  const initialCell = `${columnLetter(MEMBER_REQUEST_INITIAL_DATE_COLUMN)}${rowNumber}`;
  const responseCell = `${columnLetter(MEMBER_REQUEST_RESPONSE_DATE_COLUMN)}${rowNumber}`;
  return `NETWORKDAYS(${initialCell},${responseCell})-1`;
};

export const mapCycleToExportRow = (conversation, cycle, member = {}) => {
  const conversationMember = conversation.member || {};
  const memberMessages = cycle.memberMessages || [];
  const careMessages = cycle.careMessages || [];
  const initialMessage = memberMessages[0] || {};
  const lastMemberMessage = memberMessages[memberMessages.length - 1] || {};
  const firstResponse = careMessages[0];
  const unanswered = careMessages.length === 0;
  return [
    toLocalDate(initialMessage.createdTimestamp),
    unanswered ? toLocalDate(completionTimestamp(conversation)) : toLocalDate(firstResponse.createdTimestamp),
    unanswered ? toLocalDate(lastMemberMessage.createdTimestamp) : toLocalDate(firstResponse.createdTimestamp),
    anyAttachment(memberMessages) || anyAttachment(careMessages) ? "Yes" : "",
    getMemberName(conversation, member),
    member.clientName || conversationMember.clientName || "",
    String(member.sequencedMemberId || conversationMember.sequencedMemberId || ""),
    conversationMember.memberHccId || member.memberHccId ? "HealthRules Payer" : "Javelina",
    member.email || conversationMember.emailAddress || "",
    _global.formatPhoneNumber(member.phone || conversationMember.phoneNumber) || "",
    conversation.topic || "",
    unanswered ? "" : (firstResponse.senderId || ""),
    unanswered ? "" : toLocalDate(firstResponse.createdTimestamp),
    null,
    formatCycleMessages(memberMessages),
    unanswered ? NO_MEMBER_CARE_RESPONSE : formatCycleMessages(careMessages),
  ];
};

const memberIdentityValues = (conversation, member = {}) => {
  const conversationMember = conversation.member || {};
  return [
    getMemberName(conversation, member),
    member.clientName || conversationMember.clientName || "",
    String(member.sequencedMemberId || conversationMember.sequencedMemberId || ""),
    conversationMember.memberHccId || member.memberHccId ? "HealthRules Payer" : "Javelina",
    member.email || conversationMember.emailAddress || "",
    _global.formatPhoneNumber(member.phone || conversationMember.phoneNumber) || "",
    conversation.topic || "",
  ];
};

export const mapMemberCareInitiatedToExportRow = (conversation, message, member = {}) => [
  toLocalDate(message.createdTimestamp),
  message.senderId || message.senderFullName || "",
  hasAttachment(message.attachments) ? "Yes" : "",
  ...memberIdentityValues(conversation, member),
  messageText(message.message),
];

export const mapMessageToExportRow = (conversation, message, member = {}, threadNumber = "") => {
  const memberSender = isMemberMessage(message, conversation.recipientId);
  return [
    threadNumber,
    toLocalDate(message.createdTimestamp),
    memberSender ? "Member" : "Member Care",
    memberSender
      ? (message.senderFullName || message.senderId || "")
      : (message.senderId || message.senderFullName || ""),
    hasAttachment(message.attachments) ? "Yes" : "",
    ...memberIdentityValues(conversation, member),
    messageText(message.message),
  ];
};

const cycleMatchesExportRange = (conversation, cycle, fromDate, toDate) => {
  const firstResponse = (cycle.careMessages || [])[0];
  const timestamp = firstResponse ? firstResponse.createdTimestamp : completionTimestamp(conversation);
  return isTimestampInDateRange(timestamp, fromDate, toDate);
};

const memberRequestSortTime = (row) => {
  const value = row[12] instanceof Date ? row[12] : row[1];
  return value instanceof Date ? value.getTime() : 0;
};

export const mapConversationCyclesToExportRows = (conversation, messages, member = {}, fromDate, toDate) =>
  buildCycles(messages, conversation.recipientId)
    .filter((cycle) => cycleMatchesExportRange(conversation, cycle, fromDate, toDate))
    .map((cycle) => mapCycleToExportRow(conversation, cycle, member))
    .sort((a, b) => memberRequestSortTime(a) - memberRequestSortTime(b));

const compareAllMessageGroups = (a, b, member) => {
  const earliestDiff = (a.messages[0].createdTimestamp || 0) - (b.messages[0].createdTimestamp || 0);
  if (earliestDiff) return earliestDiff;
  const nameDiff = getMemberName(a.conversation, member).localeCompare(getMemberName(b.conversation, member));
  if (nameDiff) return nameDiff;
  return String(a.conversation.conversationId || "").localeCompare(String(b.conversation.conversationId || ""));
};

export const collectCompleteExportRows = (conversations, messagesByConversationId, member = {}, fromDate, toDate) => {
  const memberRequests = [];
  const memberCareInitiated = [];
  const allMessageGroups = [];

  conversations.forEach((conversation) => {
    const messages = messagesByConversationId.get(conversation.conversationId) || [];
    const classified = classifyMessages(messages, conversation.recipientId);

    classified.cycles
      .filter((cycle) => cycleMatchesExportRange(conversation, cycle, fromDate, toDate))
      .forEach((cycle) => memberRequests.push(mapCycleToExportRow(conversation, cycle, member)));

    classified.memberCareInitiated
      .filter((message) => isTimestampInDateRange(message.createdTimestamp, fromDate, toDate))
      .forEach((message) => memberCareInitiated.push(
        mapMemberCareInitiatedToExportRow(conversation, message, member)
      ));

    const includedMessages = classified.ordered
      .filter((message) => isTimestampInDateRange(message.createdTimestamp, fromDate, toDate));
    if (includedMessages.length) allMessageGroups.push({ conversation, messages: includedMessages });
  });

  memberRequests.sort((a, b) => memberRequestSortTime(a) - memberRequestSortTime(b));
  memberCareInitiated.sort((a, b) => a[0] - b[0]);
  allMessageGroups.sort((a, b) => compareAllMessageGroups(a, b, member));
  const allMessages = [];
  allMessageGroups.forEach((group, index) => {
    group.messages.forEach((message) => {
      allMessages.push(mapMessageToExportRow(group.conversation, message, member, index + 1));
    });
  });

  return { memberRequests, memberCareInitiated, allMessages };
};

export const fetchConversationMessages = async (conversation, getMessagesByConversationId) => {
  if (String(conversation.conversationId || "").includes("LOCAL_")) {
    return conversation.lastMessage ? [conversation.lastMessage] : [];
  }
  if (conversation.conversationVersion === 3 && Array.isArray(conversation.messages) && conversation.messages.length > 0) {
    return conversation.messages;
  }

  const items = [];
  let lastEvaluatedKey;
  do {
    const results = await getMessagesByConversationId(
      conversation.conversationId,
      lastEvaluatedKey,
      conversation.recipientId,
    );
    const page = results?.data || {};
    items.push(...(page.Items || []));
    lastEvaluatedKey = page.LastEvaluatedKey;
  } while (lastEvaluatedKey);

  return items;
};

export const fetchAllConversationMessages = async (
  conversations,
  getMessagesByConversationId,
  concurrency = FETCH_CONCURRENCY,
) => {
  const results = new Map();
  const queue = [...conversations];
  const workerCount = Math.min(Math.max(concurrency, 1), queue.length);
  const workers = Array.from({ length: workerCount }, async () => {
    while (queue.length) {
      const conversation = queue.shift();
      const messages = await fetchConversationMessages(conversation, getMessagesByConversationId);
      results.set(conversation.conversationId, messages);
    }
  });
  await Promise.all(workers);
  return results;
};
